import { SupportType } from "@faire/help-center-utils/lib/dateInTimeZone";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { logWarning } from "@faire/web--source/common/logging";
import { trackCxFoundationsOrderListPageBrandOrderClick } from "@faire/web-api--source/events/cxFoundations/click/orderListPageBrandOrder";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import home from "@faire/web-api--source/routes/www/support";
import { useHistory } from "react-router-dom";

import {
  StepPage,
  selfServePageToStepAndRoute,
  selfServePageToStepAndRouteWithoutOrderSelection,
} from "../view/step";

import { useNavigationStore } from "./NavigationContext";
import { useSelfServeContextStore } from "./SelfServeContext";
import { useCurrentNodeHasMoreLevels } from "./useCurrentNodeHasMoreLevels";
import { useIssueType } from "./useIssueType";

type NextStepParams = {
  brandOrder?: ISelfServeBrandOrder;
  supportType?: SupportType;
  skipOrderSelection?: boolean;
};

export const useNavigation = () => {
  const [{ currentPage }, setNavigationStore] = useNavigationStore([
    "currentPage",
  ]);
  const [{ brandOrders, selectedBrandOrder }] = useSelfServeContextStore([
    "brandOrders",
    "selectedBrandOrder",
  ]);
  const { hasMoreIssueLevels } = useCurrentNodeHasMoreLevels(1);
  const { hasMoreIssueLevels: latestNodeHasMoreLevels } =
    useCurrentNodeHasMoreLevels();
  const history = useHistory();
  const { issueType } = useIssueType();

  const goToPreviousStep = () => {
    if (!currentPage) {
      logWarningUnexpectedPath(currentPage);
      return;
    }
    switch (currentPage) {
      case StepPage.TOPIC_SELECTION:
        history.push(home());
        break;
      case StepPage.ORDER_SELECTION:
        navigate(StepPage.TOPIC_SELECTION);
        break;
      case StepPage.TELL_US_MORE:
        goToPreviousStepFromTellUsMoreStep();
        break;
      case StepPage.ANSWER:
        goToPreviousStepFromAnswerStep();
        break;
      case StepPage.EMAIL_FORM:
      case StepPage.PHONE_FORM:
        navigate(StepPage.ANSWER);
        break;
      case StepPage.EMAIL_SUBMISSION_CONFIRMATION:
        navigate(StepPage.EMAIL_FORM);
        break;
      case StepPage.PHONE_SUBMISSION_CONFIRMATION:
        navigate(StepPage.PHONE_FORM);
        break;
      default:
        logWarningUnexpectedPath(history.location.pathname);
    }
  };

  const goToNextStep = ({
    brandOrder,
    supportType,
    skipOrderSelection,
  }: NextStepParams = {}) => {
    if (!currentPage) {
      logWarningUnexpectedPath(currentPage);
      return;
    }
    switch (currentPage) {
      case StepPage.ISSUE_SEARCH:
        goToNextStepFromIssueSearchStep();
        break;
      case StepPage.TOPIC_SELECTION:
        goToNextStepFromTopicSelectionStep(skipOrderSelection);
        break;
      case StepPage.ORDER_SELECTION:
        goToNextStepFromOrderSelectStep(brandOrder);
        break;
      case StepPage.TELL_US_MORE:
        navigate(StepPage.ANSWER);
        break;
      case StepPage.ANSWER:
        goToNextStepFromAnswerStep(supportType);
        break;
      case StepPage.EMAIL_FORM:
        navigate(StepPage.EMAIL_SUBMISSION_CONFIRMATION);
        break;
      case StepPage.PHONE_FORM:
        navigate(StepPage.PHONE_SUBMISSION_CONFIRMATION);
        break;
      default:
        logWarningUnexpectedPath(history.location.pathname);
    }
  };

  const goToNextStepFromAnswerStep = (supportType?: SupportType) => {
    if (supportType === "email") {
      navigate(StepPage.EMAIL_FORM);
    } else if (supportType === "phone") {
      navigate(StepPage.PHONE_FORM);
    }
  };

  const goToNextStepFromTopicSelectionStep = (skipOrderSelection?: boolean) => {
    if (
      brandOrders &&
      brandOrders.length > 0 &&
      !selectedBrandOrder &&
      !skipOrderSelection
    ) {
      navigate(StepPage.ORDER_SELECTION);
    } else {
      navigateAheadToStep3or4();
    }
  };

  const goToNextStepFromIssueSearchStep = () => {
    if (latestNodeHasMoreLevels) {
      navigate(StepPage.TELL_US_MORE);
    } else {
      navigate(StepPage.ANSWER);
    }
  };

  const goToNextStepFromOrderSelectStep = (
    brandOrder?: ISelfServeBrandOrder
  ) => {
    const retailerOrBrandToken = getRetailerOrBrandToken();
    trackCxFoundationsOrderListPageBrandOrderClick(
      brandOrder?.brand_order?.token ?? "",
      retailerOrBrandToken,
      issueType
    );
    navigateAheadToStep3or4();
  };

  const goToPreviousStepFromTellUsMoreStep = () => {
    navigateBackToStep2or1();
  };

  const goToPreviousStepFromAnswerStep = () => {
    if (hasMoreIssueLevels) {
      navigate(StepPage.TELL_US_MORE);
    } else {
      navigateBackToStep2or1();
    }
  };

  const navigateAheadToStep3or4 = () => {
    if (hasMoreIssueLevels) {
      navigate(StepPage.TELL_US_MORE);
    } else {
      navigate(StepPage.ANSWER);
    }
  };

  const navigateBackToStep2or1 = () => {
    if (brandOrders && brandOrders.length > 0) {
      navigate(StepPage.ORDER_SELECTION);
    } else {
      navigate(StepPage.TOPIC_SELECTION);
    }
  };

  const navigate = (page: StepPage) => {
    if (brandOrders && brandOrders.length > 0) {
      setNavigationStore({
        currentStep: selfServePageToStepAndRoute[page].step,
        currentPage: page,
      });
      history.push(selfServePageToStepAndRoute[page].route, {
        from: { page: currentPage },
      });
    } else {
      setNavigationStore({
        currentStep:
          selfServePageToStepAndRouteWithoutOrderSelection[page].step,
        currentPage: page,
      });
      history.push(
        selfServePageToStepAndRouteWithoutOrderSelection[page].route,
        {
          from: { page: currentPage },
        }
      );
    }
  };

  return {
    goToPreviousStep,
    goToNextStep,
  };
};

const logWarningUnexpectedPath = (path: string) => {
  logWarning(
    "NavigationStore.goToPreviousStep invoked from unexpected path causing step to be undefined",
    { data: { path } }
  );
};
